import { useEffect, useMemo, useState } from 'react';
import { Brain, Check } from 'lucide-react';
import { Button } from '@components/common/Button';
import { Card, CardDescription, CardHeader, CardTitle } from '@components/common/Card';
import { LoadingSpinner } from '@components/common/LoadingSpinner';
import { useListRoles } from '@api/hooks/useRoles';
import {
  useMemoryVisibilityConfig,
  useUpdateMemoryVisibilityConfig,
} from '@api/hooks/useMemories';
import type { Role } from '@schemas/api';

const toggleRole = (roleIds: string[], roleId: string) =>
  roleIds.includes(roleId)
    ? roleIds.filter((id) => id !== roleId)
    : [...roleIds, roleId];

export const MemoryVisibilitySettings = () => {
  const { data: rolesData, isLoading: rolesLoading } = useListRoles(0, 1000);
  const { data: visibilityConfig, isLoading: configLoading } = useMemoryVisibilityConfig();
  const updateVisibility = useUpdateMemoryVisibilityConfig();
  const [selectedRoleIds, setSelectedRoleIds] = useState<string[]>([]);

  const roles = useMemo(
    () => (rolesData?.items ?? []).filter((role: Role) => role.name !== 'admin'),
    [rolesData?.items]
  );

  useEffect(() => {
    if (visibilityConfig) {
      setSelectedRoleIds(visibilityConfig.tenant_memory_role_ids);
    }
  }, [visibilityConfig]);

  const hasChanges = useMemo(() => {
    const currentIds = new Set(visibilityConfig?.tenant_memory_role_ids ?? []);
    const draftIds = new Set(selectedRoleIds);
    if (currentIds.size !== draftIds.size) return true;
    return [...currentIds].some((id) => !draftIds.has(id));
  }, [selectedRoleIds, visibilityConfig?.tenant_memory_role_ids]);

  if (rolesLoading || configLoading) {
    return <LoadingSpinner />;
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <Brain className="h-5 w-5 text-primary-500" />
              <CardTitle>Memory Visibility</CardTitle>
            </div>
            <CardDescription className="mt-2">
              Choose which tenant roles can view active shared memory policies. Internal observations and personal memories are not shown in the workspace.
            </CardDescription>
          </div>
          <Button
            onClick={() => updateVisibility.mutate(selectedRoleIds)}
            loading={updateVisibility.isPending}
            disabled={!hasChanges}
          >
            Save Visibility
          </Button>
        </div>
      </CardHeader>

      <div className="space-y-3">
        {roles.map((role) => {
          const selected = selectedRoleIds.includes(role.id);
          return (
            <button
              key={role.id}
              type="button"
              onClick={() => setSelectedRoleIds((current) => toggleRole(current, role.id))}
              className="flex w-full items-center justify-between rounded-lg border border-border-default bg-surface-raised px-4 py-3 text-left transition-colors hover:border-primary-300"
            >
              <span>
                <span className="block text-sm font-semibold text-content-primary">{role.name}</span>
                <span className="mt-1 block text-xs text-content-secondary">
                  {role.description || (role.is_system_role ? 'System role' : 'Tenant custom role')}
                </span>
              </span>
              <span className={`flex h-5 w-5 items-center justify-center rounded border ${
                selected
                  ? 'border-primary-500 bg-primary-500 text-white'
                  : 'border-border-default text-transparent'
              }`}>
                <Check className="h-3.5 w-3.5" />
              </span>
            </button>
          );
        })}
      </div>
    </Card>
  );
};
